import React from 'react'

const AGENT_CONFIG = {
  research: {
    name: 'Research Agent',
    icon: '🔍',
    color: 'blue',
    description: 'Land Registry & Planning'
  },
  valuation: {
    name: 'Valuation Agent',
    icon: '💰',
    color: 'green',
    description: 'Comparable Analysis'
  },
  risk: {
    name: 'Risk Agent',
    icon: '⚠️',
    color: 'orange',
    description: 'Monte Carlo Simulation'
  },
  compliance: {
    name: 'Compliance Agent',
    icon: '📋',
    color: 'purple',
    description: 'Regulatory Checks'
  },
  underwriting: {
    name: 'Underwriting Agent',
    icon: '🎯',
    color: 'red',
    description: 'Final Recommendation'
  }
}

function AgentCard({ agentKey, agent }) {
  const config = AGENT_CONFIG[agentKey]
  const isWorking = agent.status === 'working'
  const isComplete = agent.status === 'complete'
  const isPending = agent.status === 'pending'

  const getStatusColor = () => {
    if (isComplete) return 'border-green-500 bg-green-50'
    if (isWorking) return 'border-primary-500 shadow-md'
    return 'border-gray-200'
  }

  const getProgressColor = () => {
    const colors = {
      blue: 'bg-blue-500',
      green: 'bg-green-500',
      orange: 'bg-orange-500',
      purple: 'bg-purple-500',
      red: 'bg-red-500'
    }
    return colors[config.color] || 'bg-primary-500'
  }

  return (
    <div className={`agent-card ${getStatusColor()}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{config.icon}</span>
          <div>
            <h4 className="font-bold text-gray-900">{config.name}</h4>
            <p className="text-xs text-gray-500">{config.description}</p>
          </div>
        </div>
        
        {/* Status Badge */}
        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
          isComplete ? 'bg-green-100 text-green-800' :
          isWorking ? 'bg-primary-100 text-primary-800' :
          'bg-gray-100 text-gray-600'
        }`}>
          {isComplete ? '✓ Complete' : isWorking ? '● Working' : '○ Pending'}
        </span>
      </div>

      {/* Progress Bar */}
      <div className="mb-3">
        <div className="flex justify-between text-xs text-gray-600 mb-1">
          <span>Progress</span>
          <span>{agent.progress}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className={`${getProgressColor()} h-2 rounded-full transition-all duration-500`}
            style={{ width: `${agent.progress}%` }}
          ></div>
        </div>
      </div>

      {/* Log Stream */}
      <div className="bg-gray-50 rounded p-2 h-24 overflow-y-auto">
        {agent.logs.length === 0 ? (
          <div className="text-xs text-gray-400 italic">Waiting to start...</div>
        ) : (
          agent.logs.map((log, idx) => (
            <div 
              key={idx} 
              className={`log-entry ${idx === agent.logs.length - 1 ? 'success' : ''}`}
            >
              {log}
            </div>
          ))
        )}
      </div>

      {/* Animated indicator when working */}
      {isWorking && (
        <div className="mt-3 flex items-center gap-2 text-xs text-primary-600">
          <div className="flex gap-0.5">
            <span className="animate-bounce" style={{ animationDelay: '0ms' }}>●</span>
            <span className="animate-bounce" style={{ animationDelay: '150ms' }}>●</span>
            <span className="animate-bounce" style={{ animationDelay: '300ms' }}>●</span>
          </div>
          <span>Processing...</span>
        </div>
      )}
    </div>
  )
}

function AgentPanel({ agents }) {
  return (
    <div className="h-full flex flex-col">
      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
        🤖 AI Agents
        <span className="text-sm font-normal text-gray-500">
          (Running in parallel)
        </span>
      </h3>
      
      <div className="flex-1 space-y-4 overflow-y-auto">
        {Object.entries(agents).map(([key, agent]) => (
          <AgentCard key={key} agentKey={key} agent={agent} />
        ))}
      </div>
    </div>
  )
}

export default AgentPanel
